import { Col, Card, CardBody, Row, Button, CardHeader, CardFooter, Input } from 'reactstrap';
import CommonCardHeader from '../../../coreComponents/CommonCardHeader';
import {
  FlowData,
  LeadInput,
  LeadStageStatus,
  LeadStatus,
  SingleLeadData,
} from '../../../types/Leads';
import { useTranslation } from 'react-i18next';
import { formatWithOffset } from '../../../lib/timeUtils';
import AjaxSourcedData from '../../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { getLeadsActivityTableColumns } from '../../../data/formsAndTable/tables/dataTables/DataSource';
import { useEffect, useRef, useState, Ref, useMemo } from 'react';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
// import { Gallery, Item } from 'react-photoswipe-gallery';
// import 'photoswipe/style.css';
import { validateImages } from '../../../lib/filesUtils';
import Swal from 'sweetalert2';
import {
  useGetAllCoursesQuery,
  useLeadToUserConvertMutation,
} from '../../../reduxToolkit/reducers/usersSlice';
import { randomPassword } from '../../../lib/leadsUtils';
import AddUserForm from '../../users/usersList/AddUserForm';
import { getUserAddInputs } from '../../users/usersList/inputs';
import { formDefault } from '../../users/usersList';
import { AddUserFormInputs, UserRole } from '../../../types/Forms';
import { sleep } from '../../../lib/sleep';
import CommentFileModal from '../../commetFileModal';
import { isNewPreview } from '../../../utils';
import { NoDataComponent } from '../../formsAndTable/forms/formControls/NoDataComponent';
import { ICourse } from '../../../types/Courses';
import { baseLang } from '../../../lib/utils';

type ExistingFile = {
  name?: string;
  url: string; // relative path from BE (e.g. "leads/uuid.png")
  size?: number;
  type?: string;
};

type PreviewBase = { url: string; name?: string; w?: number; h?: number };
type PreviewItem =
  | ({ kind: 'existing' } & PreviewBase)
  | ({ kind: 'new'; file: File } & PreviewBase);

type LeadActionsWrapperProps = LeadInput & {
  leadStatus: LeadStatus;
  files?: ExistingFile[];
  onStageStatusChange: (payload: {
    flowId: number;
    status: LeadStageStatus;
  }) => Promise<void> | void;
  onAddComment: (formData: FormData) => Promise<void> | void;
  onRemoveExistingFile?: (payload: { flowId: number; url: string }) => Promise<void> | void;
  onRowCommentClick?: (row: FlowData, flowId: number) => void;
  handleOpenCreateActionModal?: (flowId: number) => void;
  leadData: SingleLeadData;
};

const LeadActionsWrapper = (data: LeadActionsWrapperProps) => {
  const { t, i18n } = useTranslation();
  const pageLang: 'en' | 'uk' | 'ru' = baseLang(i18n.language) as 'en' | 'uk' | 'ru';
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [defaultFormValues, setDefaultFormValues] =
    useState<Partial<AddUserFormInputs>>(formDefault);
  const [openComment, setOpenComment] = useState(false);
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [currentEditDataId, setCurrentEditDataId] = useState<number | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isExamPassed, setIsExamPassed] = useState<boolean | null>(null);
  const [toDeleteFileUrl, setToDeleteFileUrl] = useState<string[] | null>(null);
  const [leadConverter] = useLeadToUserConvertMutation();
  const makeAbs = (rel?: string) =>
    rel
      ? `${process.env.REACT_APP_BACKEND_STATIC}/${rel}`
      : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

  const { data: allCourses } = useGetAllCoursesQuery({});
  // Revoke object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (isNewPreview(p)) URL.revokeObjectURL(p.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fill in image natural dimensions for PhotoSwipe (only once per URL)
  useEffect(() => {
    let cancelled = false;
    previews.forEach((p) => {
      if (p.w && p.h) return;
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setPreviews((prev) => {
          const copy = [...prev];
          const ix = copy.findIndex((x) => x.url === p.url);
          if (ix !== -1) {
            copy[ix] = { ...copy[ix], w: img.naturalWidth || 1600, h: img.naturalHeight || 1200 };
          }
          return copy;
        });
      };
      img.src = p.url;
    });
    return () => {
      cancelled = true;
    };
  }, [previews]);
  useEffect(() => {
    const pwrd = randomPassword(8);
    setDefaultFormValues({
      firstName: data.leadData.fullName,
      lastName: '',
      email: data.leadData?.email ?? 'No email',
      telegram: data.leadData.phone ?? undefined,
      password: pwrd,
      dob: new Date().toISOString().split('T')[0],
      role: UserRole.STUDENT,
      photo: undefined,
    });
    return () => {
      setDefaultFormValues({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        telegram: '',
        dob: new Date().toISOString().split('T')[0],
        role: UserRole.STUDENT,
        photo: undefined,
      });
    };
  }, []);
  const coursesListOptions = useMemo(() => {
    if (!allCourses) return [];
    if (pageLang === 'en') {
      return allCourses.map((course: ICourse) => ({
        value: String(course.id),
        label: course.name,
      }));
    } else {
      return allCourses.map((course: ICourse) => ({
        value: String(course.id),
        label: course.translations.find((t) => t.lang === pageLang)?.name || course.name,
      }));
    }
  }, [allCourses, pageLang]);
  const handleFilesSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const { valid, errors } = validateImages(files, t);
    setUploadErrors(errors);

    if (valid.length) {
      const newPreviews: PreviewItem[] = valid.map((file) => ({
        kind: 'new',
        file,
        url: URL.createObjectURL(file),
      }));
      setPreviews((prev) => [...prev, ...newPreviews]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePreview = async (p: PreviewItem) => {
    if (isNewPreview(p)) {
      URL.revokeObjectURL(p.url);
      setPreviews((prev) => prev.filter((x) => x !== p));
      return;
    }
    // existing on server
    setPreviews((prev) => prev.filter((x) => x !== p)); // optimistic
    try {
      await data.onRemoveExistingFile?.({ flowId: data.id, url: p.url });
    } catch {
      setPreviews((prev) => [p, ...prev]); // rollback
    }
  };

  const handleOpenEditComment = (id: number) => {
    setCurrentEditDataId(id);
    const flowData = data.data.find((el) => el.id === id);

    const existingFromProps: PreviewItem[] =
      (flowData?.files ?? []).map((f) => ({
        kind: 'existing',
        url: makeAbs(f.url),
        name: f.name,
      })) ?? [];

    setPreviews(existingFromProps); // reset to existing of this row
    setComment(flowData?.comment ?? '');
    setOpenComment(true);
  };

  const handleSubmitComment = async () => {
    const files = previews.filter(isNewPreview).map((p) => p.file);

    if (!comment.trim() && files.length === 0) {
      commentRef.current?.focus();
      return;
    }
    if (!currentEditDataId) return;

    const { valid, errors } = validateImages(files, t);
    if (errors.length) {
      setUploadErrors(errors);
      return;
    }
    setSubmitting(true);
    const formData = new FormData();
    formData.append('flowDataId', String(currentEditDataId));
    formData.append('flowId', String(data.id));
    formData.append('comment', comment.trim());
    files.forEach((file) => formData.append('screenshots', file));
    formData.append('toDeleteFileUrls', toDeleteFileUrl ? JSON.stringify(toDeleteFileUrl) : '[]');
    // toDeleteFileUrl?.forEach((url) => formData.append('toDeleteFileUrls', url));

    try {
      await data.onAddComment(formData);
      // clean new previews
      previews.forEach((p) => {
        if (isNewPreview(p)) URL.revokeObjectURL(p.url);
      });
      setPreviews((prev) => prev.filter((p) => p.kind === 'existing'));
      setComment('');
      setUploadErrors([]);
      setOpenComment(false);
    } finally {
      setSubmitting(false);
      setCurrentEditDataId(null);
      setToDeleteFileUrl(null);
    }
  };

  const convertLeadToUserHandler = async () => {
    const splitName = (full: string) => {
      const [first = '', ...rest] = full.trim().split(/\s+/);
      return { first, last: rest.join(' ') };
    };

    const seed = splitName(data.leadData.fullName || '');

    setDefaultFormValues((prev) => ({
      ...prev,
      firstName: seed.first,
      lastName: seed.last,
    }));
    setModalIsOpen(true);
  };

  const newUserSubmitHandler = async (newdata: any): Promise<void> => {
    console.log('Submitting new user with data:', newdata);
    const formData = new FormData();
    formData.append('firstName', newdata.firstName);
    formData.append('lastName', newdata.lastName);
    formData.append('telegram', newdata.telegram);
    formData.append('email', newdata.email);
    formData.append('password', newdata.password || '');
    formData.append('dob', newdata.dob);
    formData.append('role', newdata.role);
    formData.append('leadId', String(data.leadData.id));
    formData.append('city', newdata.city);

    if (newdata.photo instanceof File) {
      formData.append('photo', newdata.photo);
    }
    formData.append('courseIds', JSON.stringify(newdata.coursesOptions || []));
    try {
      const res = await leadConverter(formData).unwrap();
      setModalIsOpen(false);
      await sleep(500);
      if (res.user.id) {
        Swal.fire({
          title: t('leads.convert.successTitle'),
          titleText: t('leads.convert.successText', {
            user: `${res.user.firstName} ${res.user.lastName}`,
          }),
          icon: 'success',
        });
      }
    } catch (error) {
      setModalIsOpen(false);
      await sleep(500);
      Swal.fire({
        title: t('leads.convert.errorTitle'),
        titleText: t('leads.convert.errorText'),
        icon: 'error',
      });
    }
  };
  const handleDeleteFile = async (p: PreviewItem) => {
    Swal.fire({
      title: t('global.filesMutationsText.titleDelete'),
      text: t('global.filesMutationsText.textDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('global.filesMutationsText.delete'),
      cancelButtonText: t('global.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        setToDeleteFileUrl((prev) => (prev ? [...prev, p.url] : [p.url]));
        handleRemovePreview(p);
      } else if (result.isDismissed) {
        return;
      }
    });
  };
  return (
    <>
      <Row className="pt-3">
        <Card>
          <CardHeader>
            <Col sm="12" className="d-flex gap-2">
              <Col sm="6">
                <CommonCardHeader
                  title={t(`leads.flowTitle.${data.position}`)}
                  tag
                  tagClass="pb-4"
                />
              </Col>
              <Col sm="6" className="border-start ps-2">
                <Row>
                  <p>
                    {data.stageStatus === LeadStageStatus.ACTIVE
                      ? t('leads.stageStatus.notFinished')
                      : t('leads.stageStatus.finished')}{' '}
                    {formatWithOffset(data.updated, viewerTz, undefined)}{' '}
                  </p>
                </Row>
                <Row>
                  <Col sm="6">
                    <Button
                      color="primary"
                      outline
                      className="mt-2"
                      onClick={() =>
                        data.onStageStatusChange({
                          flowId: data.id,
                          status: LeadStageStatus.CLOSED,
                        })
                      }
                      disabled={data.stageStatus === LeadStageStatus.CLOSED}
                    >
                      {data.stageStatus === LeadStageStatus.ACTIVE
                        ? t('leads.buttons.closeStage')
                        : t('leads.buttons.stageClosed')}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Col>
          </CardHeader>

          <CardBody>
            <AjaxSourcedData
              hasFilter={false}
              data={data.data}
              pagination={false}
              columns={getLeadsActivityTableColumns<FlowData>(t, viewerTz, {
                task: (r) => r.task ?? '',
                taskType: (r) => r.taskType ?? '',
                deadline: (r) => r.deadline,
                updatedAt: (r) => r.updatedAt,
                comment: (r) => r.comment ?? '',
                onActionClick: (row) => handleOpenEditComment(row.id),
                filesCount: (r) => r?.files?.length ?? 0,
              })}
              noDataComponent={<NoDataComponent />}
            />
          </CardBody>

          <CardFooter>
            <Row>
              <Col sm="auto" className="ms-auto d-flex justify-content-between">
                {data.position === 2 && (
                  <Button
                    color="secondary"
                    outline
                    className="me-2"
                    onClick={convertLeadToUserHandler}
                    disabled={
                      data.leadData.status === LeadStatus.CONVERTED || !!data.leadData.userId
                    }
                  >
                    {t('leads.buttons.convertToUser')}
                  </Button>
                )}
                {data.handleOpenCreateActionModal && (
                  <Button
                    color="primary"
                    outline
                    // className="btn-link text-decoration-none"
                    onClick={() => data.handleOpenCreateActionModal?.(data.id)}
                    disabled={
                      data.leadStatus !== LeadStatus.NEW ||
                      data.stageStatus === LeadStageStatus.CLOSED
                    }
                  >
                    {t('leads.buttons.addAction')}
                  </Button>
                )}
              </Col>
            </Row>
          </CardFooter>
        </Card>
      </Row>

      {/* Comment + attachments composer */}
      <CommentFileModal
        isOpen={openComment}
        onClose={() => setOpenComment(false)}
        labels={{
          title: t('leads.commentForm.label'),
          placeholder: t('leads.commentForm.placeholder'),
          ok: t('global.ok'),
          saving: t('global.saving'),
          cancel: t('global.cancel'),
          remove: t('common.remove'),
        }}
        commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
        comment={comment}
        onCommentChange={setComment}
        hasFileInput={true}
        fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        onFilesSelected={handleFilesSelected}
        previews={previews}
        onRemovePreview={handleRemovePreview}
        uploadErrors={uploadErrors}
        submitting={submitting}
        onSubmit={handleSubmitComment}
        onFileDelete={handleDeleteFile}
      />
     
      <SimpleModal isOpen={modalIsOpen} close={() => setModalIsOpen(false)} size="xl">
        {/* {modalContent} */}
        <AddUserForm
          // inputs={userAddInputs}
          inputs={getUserAddInputs(t, undefined, coursesListOptions)}
          formTitle={t('users.usersList.createUserFormTitle')}
          formSubTitle={t('users.usersList.createUserFormSubtitle')}
          submitHandler={newUserSubmitHandler}
          defaultValues={defaultFormValues}
          mode={'create'}
          subject="user"
          coursesList={coursesListOptions}
        />
      </SimpleModal>
    </>
  );
};

export default LeadActionsWrapper;
